import _ from 'lodash'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { isElement, isLazy, isValidElementType, Lazy } from 'react-is'
import { matchPath } from 'react-router-dom'
import memoize from 'micro-memoize'
import { CircleFilledIcon } from '@panwds/icons'
import { LoadingIcon, useIsMounted } from '@panwds/react-ui'
import { setValuesInternal } from '../../framework/actions'
import cache from '../../framework/cache'
import { FAWKES_MICRO_APP_NAME, MSP_PATH_PREFIX, TSG_ID, TSG_ID_NONE } from '../../framework/constants'
import { BadgeType } from '../../framework/enums'
import { getPendo } from '../../framework/externals'
import { history } from '../../framework/history'
import { getFrameworkVar, inited } from '../../framework/states'
import {
  classNames,
  clearLoadingMask,
  ensureArray,
  isFunction,
  isNonemptyArray,
  isString,
} from '../../framework/utils'
import { getCurrentTsgId } from '../../framework/tsg'
import preference, { COMMON_LAST_VISITED_URL } from '../../framework/preference'
import { __error, __info, __isDebugOn } from '../../init/debug'
import { _timing } from '../../init/performance'
import { isEmptyObject } from '../../init/init'
import { addDataDogTimingOnce } from '../common/utils'
import { removeLoadingProgress } from '../common/LoadingPlaceholder'
import { isValidRoute } from './fawkesUtils'
import NavBadge from './NavBadge'

import type { NavItemProps } from '@panwds/react-nav'
import type { VFC, ElementType, MouseEvent, ReactNode } from 'react'
import type { NavItem, LazyFactory, NavAction, StoreState, NavBadgeProps, ValueBadgeProps, NavItemComponent } from '../../framework/microAppTypes'
import type { FrameworkVars, LoggedInUser, StateRef } from '../../framework/stateTypes'
import type { UserState } from '../../framework/stateTypes'

export { globalAvail, tsgAccessAvail, globalAndTsgAvail } from '../common/utils'

export const isLazyType = (Comp: ElementType | LazyFactory) => (
  isLazy(Comp) || (Comp as { $$typeof?: symbol }).$$typeof === Lazy
)

export const preventDefault = (e: MouseEvent<HTMLElement>) => {
  e.stopPropagation()
  e.preventDefault()
}

export const renderTitle = (title: ElementType | ReactNode | (() => string) | string | null | false | undefined): ReactNode => {
  if (title == null) {
    return null
  }
  if (isString(title)) {
    return title
  }
  if (isElement(title)) {
    return title
  }
  if (isFunction(title) && !title.length) {
    const titleResult = (title as () => string)()
    if (isString(titleResult)) {
      return titleResult
    }
  }
  if (isValidElementType(title)) {
    const Title = title as ElementType
    return <Title />
  }
  return getTitleText(title) || null
}

export const getTitleText = (title: ElementType | ReactNode | (() => string) | string | null | false | undefined): string | undefined => {
  if (title == null) {
    return undefined
  }
  if (isString(title)) {
    return title
  }
  if (isFunction(title) && !title.length) {
    const titleResult = (title as () => string)()
    if (!titleResult) {
      return ''
    }
    if (isString(titleResult)) {
      return titleResult
    }
  }
  if (Array.isArray(title)) {
    const filtered = title.filter(Boolean)
    return filtered.length ? filtered.map(getTitleText).join('') : ''
  }
  if (isElement(title)) {
    return getTitleText(title?.props?.children)
  }
  return undefined
}

export type IndexedNavItems = NavItem[] & {
  map: Map<string, NavItem>
  get: (path: string | undefined, exact?: boolean) => NavItem | undefined
  has: (path: string | undefined, exact?: boolean) => boolean
}

export const mergeActions = (navActions: NavAction[]): NavAction[] => {
  const mergeMapping = {} as Record<string, NavItem>
  const newNav = [] as NavAction[]
  const bottomMostActions = navActions.filter(each => each?.bottomMost)
  if (bottomMostActions.length) {
    navActions = navActions.concat(bottomMostActions.map(({ key }) => ({ key, asPlaceholder: true } as NavAction)))
  }
  for (const item of navActions) {
    if (!item) {
      continue
    }
    // TODO: remove once fawkes stop bundle them
    if (item.appName === FAWKES_MICRO_APP_NAME && item.key &&
      ['announcements', 'notifications', 'coveo'].includes(item.key)) {
      continue // ignore those form fawkes for scm
    }
    const existing = mergeMapping[item.key]
    if (!existing) { // new
      const newItem = { ...item } // make a copy for allow merge
      if (item.key) {
        mergeMapping[item.key] = newItem
      }
      newNav.push(newItem)
      continue
    } // else merge in
    if (item._override_existing) { // override existing
      existing.asPlaceholder = true // mark existing one as placeholder
    }
    else if (!(existing.asPlaceholder || item.asPlaceholder)) { // not allow merge
      console.error('WARN: ignore duplicated nav action', item, 'found existing item with same key or path without mergeNav or asPlaceholder', existing)
      continue
    }
    const rest = { ...item }
    if (rest.asPlaceholder) { // placeholder after existing
      delete rest.asPlaceholder
      Object.assign(rest, existing, {
        _placeholder_applied: true,
      }) // merge to placeholder
      if (existing.asPlaceholder || existing._placeholder_applied) { // already has placeholder applied
        Object.assign(existing, rest)
        rest.asPlaceholder = true // remove the new one instead of existing
        continue
      }
      if (item.key) {
        mergeMapping[rest.key] = rest
      }
      newNav.push(rest)
      existing.asPlaceholder = true // and mark the existing one to remove
      continue
    }
    // start merge
    Object.assign(existing, rest)
    if (existing.asPlaceholder) { // existing is placeholder
      existing._placeholder_applied = true
      existing.asPlaceholder = undefined // remove the flag
    }
  }
  const keyMap = {} as Record<string, NavItem>
  return newNav.filter(item => {
    if (item.asPlaceholder || !item.key) {
      return false
    }
    const existing = keyMap[item.key]
    if (existing) {
      console.error('found duplicated key after merge nav actions', item.key, existing, item)
      return false
    }
    keyMap[item.key] = item
    return true
  })
}

export const getSubNavListUrlWithDefaultOpen = (item: NavItem, activeSubNavItem?: NavItem): string => {
  if (item.path) {
    return item.path
  }
  if (item.defaultOpen) {
    return '/'
  }
  const isActive = (child: NavItem) => {
    if (child === activeSubNavItem) {
      return true
    }
    if (child.children) {
      return child.children.some(isActive)
    }
    return false
  }
  if (isActive(item)) {
    return '/' // url empty or / will opened by default
  }
  return '//' // not opened by default
}

export const getOverriddenNav = (leftMainNavigation: NavItem[], matchPath: string): NavItem[] => {
  const overriddenNav = leftMainNavigation.reduce((acc: NavItem[], item: NavItem) => {
    if (item.overrideLeftNav?.match) {
      const matchValue = item[item.overrideLeftNav.match]
      if (matchValue && item.overrideLeftNav.match === 'path' && matchPath.startsWith(matchValue)) {
        const overrideItems = ensureArray<NavItem>(item.overrideLeftNav.with ? item[item.overrideLeftNav.with] : item)
        const compDefaults = {} as Partial<NavItem>
        if (item?.component) {
          compDefaults.component = item.component
          if (item.contentClassName) {
            compDefaults.contentClassName = item.contentClassName
          }
        }
        const needDefaultComp: ((child: NavItem) => boolean) = !item.path || item.path.startsWith('#') ? () => true :
          (child) => !child.path?.startsWith(item.path!)
        acc.push(...overrideItems.map(child => {
          if (compDefaults.component && !child.component && needDefaultComp(child)) {
            return { ...compDefaults, ...child } // copy component from parent if path not matched
          }
          return { ...child }
        }))
        const accNav = acc as unknown as NavItem
        if (item.overrideLeftNav.logo) {
          accNav.logo = item.overrideLeftNav.logo
        }
        if (item.overrideLeftNav.collapsedLogo) {
          accNav.collapsedLogo = item.overrideLeftNav.collapsedLogo
        }
        accNav.overrideLeftNav = item.overrideLeftNav
        accNav.matchedPath = matchValue
        if (item?.component) {
          accNav.component = item.component
        }
      }
    }
    return acc
  }, [] as NavItem[])

  return overriddenNav
}

export const ensureAbsoluteRoute = (overriddenNav: NavItem[]): NavItem[] => overriddenNav.map((nav: NavItem) => {
  if (nav.path !== '#' && Array.isArray(nav.children) && nav.children.length) {
    nav.children.forEach((child: NavItem) => {
      if (child.path && nav?.path && !child.path.startsWith('#') && !child?.path?.startsWith('/')) {
        child.path = `${nav.path}/${child.path}`
      }
    })
    return {
      ...nav,
      alternateKey: nav.alternateKey || nav.path,
    }
  }
  return nav
})

export const getDisplayNavItems = (leftNavigation: NavItem[]): IndexedNavItems => {
  if (!Array.isArray(leftNavigation)) {
    return Object.defineProperties([] as NavItem[], {
      map: { value: new Map() },
      get: {
        value: () => undefined
      },
      has: {
        value: () => false
      },
    }) as IndexedNavItems
  }
  // ensure clone
  const filteredItems = leftNavigation.filter(each => each && !each.hidden && !each.isLabel && !(each.overrideLeftNav?.with === 'children'))
  const pathMap = new Map<string, NavItem>()

  const isDisabled = (item: NavItem) => {
    if (item.disabled === true) {
      return true
    }
    if (item.disabled === false) {
      return false
    }
    if (isFunction(item.disabled)) {
      return item.disabled() || false
    }
    return false
  }

  const excludedReasonMap = __isDebugOn() ? new Map() : undefined
  const recursiveMapFn = (item: NavItem, parent: NavItem | undefined = undefined): NavItem | undefined => {
    if (!item) { // filter out falsy
      return undefined
    }
    if (item.hidden || !isValidRoute(item)) {
      // filter out hidden item or avail return false
      excludedReasonMap?.set(item, {
        _reason: item.hidden ? 'hidden' : 'avail() or globalAvail() or tsgAccessAvail() return false'
      })
      return undefined
    }

    const { children, isLoading, ...copy } = item
    copy.breadcrumbs = parent && parent?.breadcrumbs ? [...parent?.breadcrumbs, getTitleText(item?.title)] : [getTitleText(item?.title)]
    if (isNonemptyArray(item.children)) {
      if ((item.layout || item.expandable === false) ? item.component : (item.component as any)?.layout) {
        // if layout is defined, children should be ignored
        copy.children = undefined
      }
      else {
        const children = item.children.map((child: NavItem) => recursiveMapFn(child, copy))
        copy.children = children.filter(Boolean) as NavItem[]
        if (excludedReasonMap && copy.children.length !== item.children.length) {
          const filteredOutItems = item.children.map(item => {
            const excluded = excludedReasonMap.get(item)
            if (excluded) {
              return { ...item, ...excluded }
            }
            return undefined
          }).filter(Boolean)
          Object.defineProperties(copy, {
            _filtered_out_items: { value: filteredOutItems },
          })
        }

        if (isFunction(copy.overrideItemForDisplay)) {
          const overrideItem = copy.overrideItemForDisplay(copy) as NavItem | undefined
          if (!overrideItem) {
            return undefined
          }
          else if (copy !== overrideItem) {
            Object.assign(copy, overrideItem)
          }
          delete copy.overrideItemForDisplay
        }
        if (!isNonemptyArray(copy.children) && copy.expandable !== false) {
          // if children is empty, this item itself should be filtered out
          excludedReasonMap?.set(item, {
            _reason: 'children all filtered out',
            _filtered_out_items: copy._filtered_out_items,
          })
          return undefined
        }
      }
    }
    copy.disabled = isDisabled(item)
    copy.isLoading = (isLoading as boolean === true) || Boolean(isLoading && isLoading?.())
    const navBadgeProps = isFunction(copy.badge) ? copy.badge() : copy.badge ? { ...copy.badge as NavBadgeProps } : undefined
    if (navBadgeProps) {
      copy.badgeProps = navBadgeProps
      const badgeType = navBadgeProps.type
      const valueBadgeProps = badgeType === BadgeType.VALUE ? navBadgeProps?.badgeProps as ValueBadgeProps : null
      if (valueBadgeProps && (valueBadgeProps.value as number) > 0) {
        valueBadgeProps.value = +valueBadgeProps.value!
      }
    }
    else if (copy?.children) {
      assignParentBadgeProps(copy)
    }
    if (copy.path && copy.path !== '#' && !copy.asPlaceholder) {
      const key = copy.exact ? `${copy.path}!exact` : copy.path
      const existing = pathMap.get(key)
      if (!existing) {
        pathMap.set(key, copy)
      }
      else if (!copy.children?.includes(existing)) { // allow share path in parent>child
        console.error('ignore duplicate path', key, existing, copy)
        excludedReasonMap?.set(item, {
          _reason: 'ignore duplicate path',
          _found: existing,
        })
        return undefined
      }
    }

    if (isFunction(copy.overrideItemForDisplay)) {
      const overrideItem = copy.overrideItemForDisplay(copy) as NavItem | undefined
      if (overrideItem?.overrideItemForDisplay) {
        delete overrideItem.overrideItemForDisplay
      }
      return overrideItem
    }

    return copy as NavItem // ensure clone
  }

  const displayItems = filteredItems.map((navItem) => recursiveMapFn(navItem)).filter(Boolean) as NavItem[]
  const filteredOutItems = excludedReasonMap ? leftNavigation.map(item => {
    const excluded = excludedReasonMap.get(item)
    if (excluded) {
      return { ...item, ...excluded }
    }
    return undefined
  }).filter(Boolean) : undefined
  Object.defineProperties(displayItems, {
    pathMap: { value: pathMap },
    get: {
      value: (path: string | undefined, exact: boolean = false) => {
        const key = path && exact === true ? `${path}!exact` : path
        return key ? pathMap.get(key) : undefined
      }
    },
    has: {
      value: (path: string | undefined, exact: boolean = false) => {
        const key = path && exact === true ? `${path}!exact` : path
        return key ? pathMap.has(key) : undefined
      }
    },
    ...(filteredOutItems && {
      _filtered_out_items: { value: filteredOutItems },
    }),
  })
  return displayItems as IndexedNavItems
}

const assignParentBadgeProps = (copy: NavItem) => {
  // Parent doesn't have a badge property of its own.
  // In this case, the parent gets assigned a badge based on its children's badges.
  const childrenWithBadges = copy?.children?.filter((child: NavItem) => child?.badgeProps?.propagateToParent === true)
  const defaultBadgeProps = {
    type: BadgeType.ICON,
    badgeProps: {
      icon: <CircleFilledIcon size='sm' className='spf-text-red-500' />
    },
    propagateToParent: true
  }
  if (childrenWithBadges && childrenWithBadges.length) {
    if (childrenWithBadges.length === 1) {
      copy.badgeProps = { ...childrenWithBadges[0].badgeProps }
      return
    }
    const [childrenWithValueBadge, childrendWithIconBadge] = _.partition(childrenWithBadges, (child) => child.badgeProps.type === BadgeType.VALUE)
    if (childrenWithValueBadge.length > 0 && childrendWithIconBadge.length > 0) {
      copy.badgeProps = defaultBadgeProps
      return
    }
    if (childrenWithValueBadge.length > 0) {
      const [stringValue, numericValue] = _.partition(childrenWithValueBadge, child => (child.badgeProps.value as number) > 0)
      if (stringValue.length && numericValue.length) {
        copy.badgeProps = defaultBadgeProps
        return
      }
      if (numericValue.length) {
        // Parent with children having only Numeric value badges.
        // Sum all the children values and display the sum value at the parent.
        const value = childrenWithValueBadge.reduce((sum: Number, child: NavItem) => sum + child.badgeProps?.badgeProps?.value, 0)
        copy.badgeProps = {
          type: BadgeType.VALUE,
          badgeProps: {
            value,
            shape: 'circle'
          },
          propagateToParent: true
        }
        return
      }
      if (stringValue.length > 0) {
        copy.badgeProps = defaultBadgeProps
        return
      }
    }
  }
}

const findActiveNavItem = (leftNavDisplayItems: NavItem[], curLocationPath: string): NavItem | undefined => {
  const recursiveFindActive = (items: NavItem[]): NavItem | undefined => {
    let mostMatched: NavItem | undefined
    for (let item of items) {
      if (item.children) { // depath first
        const activeChild = recursiveFindActive(item.children)
        if (activeChild) {
          item = activeChild
        }
        else {
          continue
        }
      }
      else if (!item.path || item.path === '#') {
        continue
      }
      else { // leaf node
        const { exact, path } = item
        const match = matchPath(curLocationPath, { exact, path })
        if (!match) {
          continue
        }
        if (match.isExact) {
          return item
        }
      }
      if (!mostMatched || mostMatched.path!.length < item.path!.length) {
        mostMatched = item
      }
    }
    return mostMatched
  }
  const activeItem = recursiveFindActive(leftNavDisplayItems)
  return activeItem
}

export type IsActive = (item: NavItem) => boolean
export const useActiveItems = (leftNavDisplayItems: NavDisplayItems) => {
  const curLocationPath = window.location.pathname
  const [activeNavItem, activeSubNavItem] = useMemo(() => {
    const navItems = leftNavDisplayItems.topItems.concat(leftNavDisplayItems.bottomActions.filter(act => act.children))
    const activeSubNavItem = findActiveNavItem(navItems, curLocationPath)
    const isActive: IsActive = !activeSubNavItem ? () => false : (item) => (Array.isArray(item.children) ? item.children.some(isActive) : item === activeSubNavItem)
    const activeNavItem = navItems.find(isActive)
    return [activeNavItem, activeSubNavItem]
  }, [leftNavDisplayItems, curLocationPath])
  useEffect(() => {
    setValuesInternal({
      activeNavItem,
      activeSubNavItem,
    })
  }, [activeNavItem, activeSubNavItem])
  return [activeNavItem, activeSubNavItem]
}

const getAbsPath = (item: NavItem, parent: NavItem | null | undefined): string | undefined => {
  if (!item?.path) {
    return
  }
  if (item.path.startsWith('/')) { // is abs already
    return item.path
  }
  if (!parent?.path) {
    __error('relative path must have parent path', item, parent)
    return
  }
  const parentPath = parent.path.endsWith('/') ? parent.path : `${parent.path}/`
  return `${parentPath}${item.path}`
}

export const generateRoutes = (navigation: NavItem[] | undefined, leftMainRoutes: NavItem[] | undefined) => {
  if (!navigation && !leftMainRoutes) {
    return [] as NavItem[]
  }

  const navRoutesMap = new Map<string, NavItem>()
  const recersiveGenerate = (item: NavItem, parent?: NavItem) => {
    const { children, path, ...rest } = item
    if (isNonemptyArray(children)) {
      for (const child of children) {
        recersiveGenerate(child, item)
      }
    }

    if (!item?.path || item.path === '#') { // ignore invalid path
      // __info('ignore invalid path for routes', item)
      return
    }
    if (item.path === '/') { // skip invalid path
      __info('skip invalid path / for routes', item)
      return
    }

    const absPath = item.component && getAbsPath(item, parent)
    if (absPath) {
      const key = item.exact ? `${absPath}!exact` : absPath
      const copied = { path: absPath, ...rest } // so path in front easier for debug
      if (!item._theme_override && parent?._theme_override) { // pass down theme override
        (copied as any)._theme_override = parent._theme_override
      }
      const existingRoute = navRoutesMap.get(key)
      if (!existingRoute) {
        navRoutesMap.set(key, copied)
      }
      else {
        if (existingRoute?.exact !== item?.exact) {
          navRoutesMap.set(key, copied)
        }
      }
    }
  }

  const mergedNavigation = [navigation, leftMainRoutes].flat(1).filter(Boolean) as NavItem[]
  for (const item of mergedNavigation) {
    recersiveGenerate(item)
  }

  const navRoutes = Array.from(navRoutesMap.keys()).sort().reverse().map(key => navRoutesMap.get(key) as NavItem)
  __info('navRoutes', navRoutes)
  return navRoutes
}

const STATE_CHANGE_THROTTLE = 300
const useStateChanged = (state: any) => {
  const [curState, setState] = useState<any>(state)
  const isMounted = useIsMounted()
  const setStateStampThrottled = useMemo(() => _.throttle((state) => {
    if (isMounted.current) {
      setState(state)
    }
  }, STATE_CHANGE_THROTTLE, { leading: false, trailing: true }), [isMounted])
  useEffect(() => {
    setStateStampThrottled(state)
  }, [setStateStampThrottled, state])
  return curState
}

const useNavDisplayTopItems = (leftNavigation: NavItem[], stateChanged: any) => {
  const itemsRef = useRef<IndexedNavItems>()
  itemsRef.current = useMemo(() => {
    const newLeftItems = getDisplayNavItems(leftNavigation)
    if (__isDebugOn() && (newLeftItems.length || (newLeftItems as any)._filtered_out_items?.length)) {
      __info('Left Nav display items', newLeftItems)
    }
    if (itemsRef.current && _.isEqual(itemsRef.current, newLeftItems) && stateChanged) {
      return itemsRef.current
    }
    return newLeftItems
  }, [leftNavigation, stateChanged])
  return itemsRef.current
}

const useNavDisplayBottomActions = (leftMainActions: NavAction[] | undefined, stateChanged: any) => {
  const itemsRef = useRef<NavAction[]>()
  itemsRef.current = useMemo(() => {
    const newActionItems = getDisplayNavItems(leftMainActions as NavItem[])
    if (newActionItems.length) {
      __info('Left nav action items', newActionItems)
    }
    if (itemsRef.current && _.isEqual(itemsRef.current, newActionItems) && stateChanged) {
      return itemsRef.current
    }
    return newActionItems as NavAction[]
  }, [leftMainActions, stateChanged])
  return itemsRef.current
}

export type NavDisplayItems = {
  topItems: NavItem[]
  bottomActions: NavAction[]
}
export const useNavDisplayItems = (leftNavigation: NavItem[], leftMainActions: NavAction[] | undefined, state: any) => {
  const stateChanged = useStateChanged(state)
  const leftNavDisplayItems = useNavDisplayTopItems(leftNavigation, stateChanged)
  const leftNavDisplayActions = useNavDisplayBottomActions(leftMainActions, stateChanged)
  return {
    topItems: leftNavDisplayItems,
    bottomActions: leftNavDisplayActions,
  } as NavDisplayItems
}

export const useReadyToRenderAndClearMask = (state: StoreState, vars: FrameworkVars): string => {
  const { navRoutes, auth, main, leftMainNavigation } = state
  const isPrivateSite = Boolean(vars.private_site_origin)
  const isAuthReady = Boolean(auth && !(auth.loading && !vars.msp && !vars.hub) && !auth.initializing && (isPrivateSite ? auth.access_token : auth.instances?.tsg_id && auth.tsgs) && !_.isEmpty(auth.user)) // not wait for main now
  const isReadyToRender = vars.remove_main_state ?
    isAuthReady && !leftMainNavigation?.initializing && !navRoutes?.loading :
    isAuthReady && main?.infoLoaded && !leftMainNavigation?.initializing && !navRoutes?.loading

  useEffect(() => {
    if (isReadyToRender) {
      removeLoadingProgress()
      if (inited.starts) {
        _timing.render_first_page_start = Date.now()
        _timing.before_render_first_page_duration = _timing.render_first_page_start - inited.starts
        __info(`ready to render first page after framework init in ${_timing.before_render_first_page_duration}ms`)
        addDataDogTimingOnce('start_render_first_page', _timing.render_first_page_start)
        addDataDogTimingOnce('before_render_first_page_duration', _timing.before_render_first_page_duration)
        __info('collected sparky performance timing', _timing)
      }
    }
  }, [isReadyToRender])

  useEffect(() => {
    if (isAuthReady) {
      clearLoadingMask()
    }
  }, [isAuthReady])

  const removeLoading = isPrivateSite || Boolean(vars.hub)
  useLayoutEffect(() => {
    if (removeLoading) {
      removeLoadingProgress()
    }
  }, [removeLoading])

  if (!isAuthReady) {
    return 'loading'
  }
  if (!isReadyToRender) {
    return 'pending'
  }
  return 'render'
}

export const getNavBadgeProps = (navItem: NavItem) => {
  const navBadgeProps = navItem.badgeProps as NavBadgeProps | undefined
  return {
    badge: <NavBadge navItem={navItem} />,
    ...(navBadgeProps && { dotColor: (navBadgeProps.badgeProps as ValueBadgeProps)?.color || 'red' }),
    ...(navBadgeProps && { dotValue: (navBadgeProps.badgeProps as ValueBadgeProps)?.value }), // it will round up to 9+ automatically
  } as Partial<NavItemProps>
}

export const usePendoIdentity = (loggedInUser: UserState | LoggedInUser | undefined, stateRef: StateRef, vars: FrameworkVars) => {
  const { main, auth } = stateRef.current
  const tsg_id = auth?.tsg_id || ''
  const id = useMemo(() => {
    if (vars.private_site_origin) {
      return ''
    }
    const tenantCspId = main?.tenant?.supportAccountId
    if (tenantCspId && +tenantCspId > 0) {
      return String(tenantCspId)
    }
    const { supportAccountIds, currentSupportAccountId } = loggedInUser || {}
    if (currentSupportAccountId && +currentSupportAccountId > 0) { // non-tsg only
      return String(currentSupportAccountId)
    }
    const instCspId = auth?.instances?.find(instance => +instance.support_account_id > 0)?.support_account_id
    if (instCspId && +instCspId > 0) {
      return String(instCspId)
    }
    const lastCspId = _.last(supportAccountIds)?.accountid
    if (lastCspId && lastCspId > 0) {
      return String(lastCspId)
    }
    return ''
  }, [vars.private_site_origin, main?.tenant?.supportAccountId, loggedInUser, auth?.instances])

  const [email, displayName, userSub] = useMemo(() => {
    const { firstname, lastname, email = '', subname } = loggedInUser || {}
    const displayName = `${firstname} ${lastname}`.trim() || email || ''
    return [email, displayName, subname]
  }, [loggedInUser])

  useEffect(() => {
    const pendo = getPendo()
    if (!pendo || !email) {
      return
    }
    const account = id ? {
      id,
      tsg_id,
    } : undefined
    if (!pendo.isReady?.()) {
      const maskedEmail = getFrameworkVar('mask_pendo_email') ? `${userSub || 'masked'}@${email.split('@', 2)[1]}` : email
      __info('init pendo', maskedEmail, displayName, account)
      pendo.initialize({
        visitor: {
          id: email,
          email,
          displayName,
        },
        account,
      })
    }
    else if (account) { // if (pendo.getAccountId?.() !== id)
      __info('update pendo account', account)
      pendo.identify({
        account,
      })
    }
  }, [id, tsg_id, email, displayName, userSub])
}

export const debouncedCacheNavDisplay = _.debounce((tsg_id: string, leftMainNavigation: NavItem[]) => {
  const CACHE_PATH_LIST = ['/', MSP_PATH_PREFIX]
  const getCacheNavItem = (navItem: NavItem): NavItem | undefined => {
    const { navPath, key, path, title: rawTitle, disabled: rawDisabled } = navItem
    const disabled = rawDisabled || undefined
    const title = getTitleText(rawTitle) || ''
    if (navItem.children && !navItem._hoisted_child &&
      !(navItem.component as any)?.layout && navItem.expandable !== false) {
      const children = navItem.children.map(item => {
        if (item.component || !navItem.component) {
          return item
        }
        return {
          ...item,
          component: navItem.component,
        }
      }).map(getCacheNavItem).filter(Boolean) as NavItem[]
      return {
        key,
        disabled,
        path,
        navPath,
        title,
        children,
      }
    }

    const component = navItem.component || navItem._hoisted_child?.component
    if (component && path) { // even if navItem.children is present
      return {
        key,
        disabled,
        path,
        navPath,
        title,
      }
    }
  }
  const navPathSet = leftMainNavigation.reduce((set, navItem) => {
    if (navItem.overrideLeftNav?.match) {
      const matchValue = navItem[navItem.overrideLeftNav.match]
      if (matchValue) {
        set.add(matchValue)
      }
    }
    return set
  }, new Set<string>())
  const eachFn = (items: NavItem[]) => getDisplayNavItems(ensureAbsoluteRoute(items)).map(getCacheNavItem).filter(Boolean) as NavItem[]
  const defaultNav = eachFn(leftMainNavigation)
  const cached = {} as Record<string, NavItem[] | null>
  if (defaultNav.length) {
    cached['/'] = defaultNav
  }
  for (const path of Array.from(navPathSet)) {
    if (!CACHE_PATH_LIST.includes(path)) {
      cached[path] = null // keep the key but null to indicate no cache
      continue
    }
    let overriddenNav = getOverriddenNav(leftMainNavigation, path) as NavItem[] & {
      component?: NavItemComponent
    }
    if (overriddenNav.length) {
      if (overriddenNav.component) {
        overriddenNav = overriddenNav.map(item => (
          {
            component: overriddenNav.component,
            ...item,
          } as NavItem
        ))
      }
      cached[path] = eachFn(overriddenNav)
    }
  }
  if (!isEmptyObject(cached)) {
    const cacheKey = `left-nav-cache/${tsg_id}`
    cache.get<Record<string, NavItem[] | null>>(cacheKey).then((existingCached) => {
      if (existingCached) { // merge with existing
        for (const path of Object.keys(existingCached)) {
          if (!cached[path] && existingCached[path]) {
            cached[path] = existingCached[path]
          }
        }
      }
      __info('cache left nav display items', cached)
      cache.set(cacheKey, cached)
    })
  }
}, 5000) // 5s

const getShellOverrideConfigFn = memoize((pathname: string) => {
  const config = getFrameworkVar('shell_path_overrides')
  if (!config || isEmptyObject(config) || !pathname) {
    return undefined
  }
  if (config[pathname]) {
    config[pathname].path = pathname
    return config[pathname]
  }
  const overridePaths = Object.keys(config).filter(path => path.startsWith('/') && path.length > 1)
  const foundPath = overridePaths.filter(path => (
    pathname === path || pathname.startsWith(path.endsWith('/') ? path : `${path}/`)
  )).reduce((a, b) => ( // get the longest
    a.length > b.length ? a : b
  ), '') || '/' // default is /
  config[foundPath].path = foundPath
  return config[foundPath]
}, { maxSize: 10 })
export const getShellOverrideConfig = (pathname: string = window.location.pathname) => getShellOverrideConfigFn(pathname)

export const listenRouteChange = (fn: () => void) => {
  let stopped = false
  let lastHref = window.location.href
  const handler = () => {
    if (stopped || lastHref === window.location.href) {
      return
    }
    try {
      fn()
    }
    catch (err) {
      console.error('listenRouteChange event handler error', err)
    }
    lastHref = window.location.href
  }
  window.addEventListener('popstate', handler)
  const stopListen = history.listen(handler)

  return () => {
    stopped = true
    stopListen()
    window.removeEventListener('popstate', handler)
    lastHref = ''
  }
}
type StateChangeEvent = CustomEvent<{ url: string | URL | undefined, action: string }>
type BlockHistoryChangeParam = { href: string, action: string, pathname: string }
const blockHistoryChange = (fn: (params: BlockHistoryChangeParam) => false | void) => {
  const tryFn = (params: BlockHistoryChangeParam) => {
    try {
      return fn(params)
    }
    catch (err) {
      console.error('blockHistoryChange event handler error', err)
    }
  }
  const beforeStateChangeHandler = (e: Event) => {
    const event = e as StateChangeEvent
    if (!event.detail?.url) {
      return
    }
    const { url: newUrl, action = 'PUSH' } = event.detail
    const url: URL = (newUrl as URL).pathname ? newUrl as URL : new URL(newUrl, window.origin)
    if (url.href === window.location.href) {
      return
    }
    try {
      const result = tryFn({
        action,
        pathname: url.pathname,
        href: url.href,
      })
      if (result === false) {
        event.preventDefault()
      }
    }
    catch (err) {
      console.error('listenRouteChange event handler error', err)
    }
  }
  window.addEventListener('statechange', beforeStateChangeHandler)
  // history.block is not needed as window.history.pushState/replaceState are overridden
  // const unblock = history.block((location, action) => {
  //   const result = tryFn({
  //     action,
  //     pathname: location.pathname,
  //     href: history.createHref(location),
  //   })
  //   if (result === false) {
  //     return BLOCKING
  //   }
  // })
  return () => {
    // unblock()
    window.removeEventListener('statechange', beforeStateChangeHandler)
  }
}

const SKIP_SAVE_LAST_VISIT_URL_REGEX = /^\/(?:authentication|logout|setup|hub|activation|ztpdeviceactivation)\b/
const saveLastVisitedUrlToPreference = _.debounce(() => {
  const url = new URL(window.location.href)
  if (SKIP_SAVE_LAST_VISIT_URL_REGEX.test(url.pathname)) {
    return // skip
  }
  if (!url.pathname.startsWith(MSP_PATH_PREFIX)) {
    // for now only MSP can skip tsg id, asc is special, keep the tsg id
    const tsgId = getCurrentTsgId()
    if (tsgId && tsgId !== TSG_ID_NONE) {
      url.searchParams.set(TSG_ID, tsgId)
    }
  }
  const urlStr = url.toString()
  if (preference.get(COMMON_LAST_VISITED_URL) !== urlStr) {
    preference.set(COMMON_LAST_VISITED_URL, urlStr)
  }
}, 5000)
export const useSaveLastVisitedUrl = (enabled?: boolean) => {
  useEffect(() => {
    if (enabled) {
      saveLastVisitedUrlToPreference()
      return listenRouteChange(saveLastVisitedUrlToPreference)
    }
  }, [enabled])
}

export const useEnsureShellPathReload = (cfg: any) => {
  useEffect(() => {
    const reloadPaths = cfg ? Object.keys(cfg) : []
    let stopListen: () => void
    let unblock: () => void
    if (reloadPaths.length) {
      let lastCfg = getShellOverrideConfig()
      stopListen = listenRouteChange(() => {
        const curCfg = getShellOverrideConfig()
        if (curCfg !== lastCfg) {
          __info('force reload due to switch shell path', lastCfg.path, '=>', curCfg.path)
          window.location.reload()
        }
        lastCfg = curCfg
      })
      unblock = blockHistoryChange(({ pathname, href, action }) => {
        const curCfg = getShellOverrideConfig(pathname)
        if (curCfg !== lastCfg) {
          __info(`force ${action} with reload due to switch shell path`, lastCfg.path, '=>', curCfg.path)
          if (action === 'REPLACE') {
            window.location.replace(href)
          }
          else {
            window.location.href = href
          }
          return false
        }
      })
    }
    return () => {
      unblock?.()
      stopListen?.()
    }
  }, [cfg])
}

export const LeftNavLoadingIcon: VFC<{ className: string }> = ({ className }) => (
  <LoadingIcon className={classNames(className, 'tw-fill-current tw-animate-spin')} />
)

export const loadingLeftNavItems = [{
  key: 'loading',
  path: '/',
  title: 'Loading ...',
  disabled: true,
  icon: LeftNavLoadingIcon,
}] as NavItem[]
